'use client';

import { c as _c } from "react/compiler-runtime";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AgentProvider, CardAIMode, CardAISessionPhase, CardAISessionSummary, CardAITodo } from '@shared/all/domains/electron';
import type { Post } from '@shared/all/domains/posts';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { AlertCircle, Check, CircleCheck, CircleDashed, Loader2, MessageCircleQuestion, Play, Sparkles, TerminalSquare } from 'lucide-react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useIsElectron from '../../../hooks/useIsElectron';
import { useRunner } from '../../../hooks/useRunnerContext';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { animDelay } from '../../../utils/animDelay';
import { client } from '../../../utils/trpc';
import Avatar from '../../common/Avatar';
import Modal, { ModalHeader } from '../../HOC/Modal';
import PostItem from '../../posts/PostItem';
import RepliesContainer from '../../posts/RepliesContainer';
import CardActivity from './CardActivity';
const CardAIModal = dynamic(() => import('./CardAIModal'), {
  ssr: false
});
const BareCardModal = dynamic(() => import('./BareCardModal'), {
  ssr: false
});
type Props = {
  post: Post;
  factiiiId: number;
  spaceSlug: string;
  // Render as a static visual clone (no dnd registration, no listeners). Used
  // by BoardList to show where a card from another list will land on drop.
  isGhost?: boolean;
  // Whether the viewer can drag cards (owner/moderator). Server enforced.
  canMove?: boolean;
  // Multi-select for merging cards (owner/moderator). Server enforced.
  selectable?: boolean;
  selected?: boolean;
  // Kind of the current selection: true = cards with unpushed AI changes,
  // false = plain cards, null = nothing selected yet. No cross-merging.
  selectionHasChanges?: boolean | null;
  onToggleSelect?: (hasChanges: boolean) => void;
};
function getAIStatus(phase: CardAISessionPhase | null): {
  icon: React.ReactNode;
  title: string;
  // Border class for the card while this phase is active — kept in lockstep
  // with the icon's colour so users can read state at a glance from either.
  borderClass: string;
} {
  switch (phase) {
    case 'starting':
    case 'research':
    case 'implement':
    case 'review':
      return {
        icon: <Loader2 size={16} className="animate-spin text-primary" />,
        title: 'AI is processing — click to view',
        borderClass: 'border-primary'
      };
    case 'awaiting-input':
      return {
        icon: <MessageCircleQuestion size={16} className="animate-pulse text-amber-400" />,
        title: 'The AI has a question — click to answer',
        borderClass: 'border-amber-400'
      };
    case 'complete':
      return {
        icon: <Sparkles size={16} className="animate-pulse text-amber-400" />,
        title: 'Changes ready for review',
        borderClass: 'border-amber-400'
      };
    case 'error':
      return {
        icon: <AlertCircle size={16} className="text-destructive" />,
        title: 'AI encountered an error',
        borderClass: 'border-destructive'
      };
    case 'done':
      return {
        icon: <Check size={16} className="text-green-400" />,
        title: 'Changes pushed — click to dismiss',
        borderClass: 'border-green-400'
      };
    case 'bare-active':
      return {
        icon: <TerminalSquare size={16} className="text-primary" />,
        title: 'Bare session active — click to open',
        borderClass: 'border-primary'
      };
    case 'bare-review':
      return {
        icon: <Sparkles size={16} className="animate-pulse text-amber-400" />,
        title: 'Bare changes ready for review',
        borderClass: 'border-amber-400'
      };
    default:
      return {
        icon: <Sparkles size={16} />,
        title: 'Open this card in Claude or Codex',
        borderClass: 'border-ds-border-subtle'
      };
  }
}
const todoIcon = (status: CardAITodo['status']) => {
  switch (status) {
    case 'completed':
      return <CircleCheck size={14} className="shrink-0 text-green-400" />;
    case 'in_progress':
      return <Play size={14} className="shrink-0 text-primary" />;
    default:
      return <CircleDashed size={14} className="shrink-0 text-textSecondary" />;
  }
};
const TodoDropdown = ({
  todos,
  onViewFull,
  anchorRef,
  portalRef
}: {
  todos: CardAITodo[];
  onViewFull: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const rect = anchorRef.current?.getBoundingClientRect();
  if (!rect || typeof document === 'undefined') return null;
  return createPortal(<div ref={portalRef} className="z-popover" style={{
    position: 'absolute',
    top: rect.bottom + window.scrollY + 4,
    left: rect.left + window.scrollX
  }} onClick={e => e.stopPropagation()} onPointerDown={e_0 => e_0.stopPropagation()}>
      <div className="anim-enter max-h-60 w-60 space-y-1 overflow-y-auto rounded-md border border-ds-border-standard bg-background-primary p-2 shadow-lg [--enter-duration:200ms] [--enter-y:10px]">
        {todos.map((todo, i) => <div key={i} style={animDelay(i * 30)} className="anim-enter flex w-full items-start gap-2 rounded-md p-2 text-sm text-textSecondary/70 [--enter-duration:200ms] [--enter-x:-10px]">
            {todoIcon(todo.status)}
            <span className={`text-sm leading-snug ${todo.status === 'completed' ? 'text-textSecondary line-through' : 'text-textPrimary'}`}>
              {todo.content}
            </span>
          </div>)}
        <button style={animDelay(todos.length * 30)} onClick={onViewFull} className="anim-enter flex w-full items-center justify-center rounded-md p-2 text-sm font-emphasis text-primary transition-colors duration-200 [--enter-duration:200ms] [--enter-x:-10px] hover:bg-background-light">
          View Full Card
        </button>
      </div>
    </div>, document.body);
};
const PROVIDER_LABEL: Record<AgentProvider, string> = {
  claude: 'Claude',
  codex: 'Codex'
};

/** The CLI picker an idle card's sparkle opens. Both CLIs are always listed:
 *  a runner that isn't signed in to one is a setup problem the user should
 *  see named, not an option silently missing from the menu. With neither
 *  signed in every row is dead, so that case has to say where to go. */
const ProviderDropdown = ({
  available,
  onPick,
  anchorRef,
  portalRef
}: {
  available: AgentProvider[];
  onPick: (provider: AgentProvider) => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const rect = anchorRef.current?.getBoundingClientRect();
  if (!rect || typeof document === 'undefined') return null;
  return createPortal(<div ref={portalRef} className="z-popover" style={{
    position: 'absolute',
    top: rect.bottom + window.scrollY + 4,
    left: rect.left + window.scrollX
  }} onClick={e => e.stopPropagation()} onPointerDown={e_0 => e_0.stopPropagation()}>
      <div className="anim-enter w-48 space-y-1 rounded-md border border-ds-border-standard bg-background-primary p-2 shadow-lg [--enter-duration:200ms] [--enter-y:10px]">
        <p className="px-2 pb-1 text-[10px] font-emphasis uppercase tracking-wider text-textSecondary">
          Open a session with
        </p>
        {(['claude', 'codex'] as const).map((provider, i) => {
        const ready = available.includes(provider);
        return <button key={provider} style={animDelay(i * 30)} disabled={!ready} onClick={() => onPick(provider)} title={ready ? `Start a terminal session on this card with ${PROVIDER_LABEL[provider]}` : `This runner isn't signed in to ${PROVIDER_LABEL[provider]}`} className="anim-enter flex w-full items-center gap-2 rounded-md p-2 text-sm text-textPrimary transition-colors duration-200 [--enter-duration:200ms] [--enter-x:-10px] hover:bg-background-light disabled:cursor-not-allowed disabled:text-textSecondary/50 disabled:hover:bg-transparent">
              <TerminalSquare size={14} className="shrink-0" />
              {PROVIDER_LABEL[provider]}
              {!ready && <span className="ml-auto text-[10px] uppercase tracking-wider">
                  Not signed in
                </span>}
            </button>;
      })}
        {available.length === 0 && <p className="px-2 pt-1 text-[11px] leading-snug text-textSecondary">
            Sign in to Claude or Codex in Board AI settings first.
          </p>}
      </div>
    </div>, document.body);
};
const BoardCard = t0 => {
  const $ = _c(116);
  const {
    post,
    factiiiId,
    spaceSlug,
    isGhost: t1,
    canMove: t2,
    selectable: t3,
    selected: t4,
    selectionHasChanges: t5,
    onToggleSelect
  } = t0;
  const isGhost = t1 === undefined ? false : t1;
  const canMove = t2 === undefined ? false : t2;
  const selectable = t3 === undefined ? false : t3;
  const selected = t4 === undefined ? false : t4;
  const selectionHasChanges = t5 === undefined ? null : t5;
  const {
    width
  } = useWindowSize();
  const isMobile = width < 768;
  const isElectron = useIsElectron();
  const {
    runners,
    agent,
    config
  } = useRunner();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t6;
  if ($[0] !== searchParams) {
    t6 = searchParams?.get("card") ?? null;
    $[0] = searchParams;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const deepLinkPostId = t6;
  const hasCardAI = isElectron || runners.length > 0;
  const [modal, setModal] = useState(null);
  const [popover, setPopover] = useState(null);
  const [session, setSession] = useState(null);
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const [todos, setTodos] = useState(t7);
  const [detailOpen, setDetailOpen] = useState(false);
  const uniqueId = `${factiiiId}-${spaceSlug}`;
  const {
    fetchPostReplies
  } = usePostsShared(client, uniqueId);
  let t8;
  if ($[3] !== fetchPostReplies || $[4] !== post.id || $[5] !== post.replies?.length || $[6] !== post.replies_count) {
    t8 = () => {
      setDetailOpen(true);
      if (post.replies_count > 0 && !post.replies?.length) {
        fetchPostReplies(post.id);
      }
    };
    $[3] = fetchPostReplies;
    $[4] = post.id;
    $[5] = post.replies?.length;
    $[6] = post.replies_count;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const openDetail = t8;
  const dropdownRef = useRef(null);
  const portalRef = useRef(null);
  let t9;
  if ($[8] !== deepLinkPostId || $[9] !== post.id || $[10] !== session?.mode) {
    t9 = () => {
      if (deepLinkPostId !== String(post.id)) {
        return;
      }
      const el = document.querySelector(`[data-post-id="${window.CSS.escape(String(post.id))}"]`);
      el?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center"
      });
      if (session?.mode === "claude") {
        setModal({
          mode: "claude"
        });
      }
    };
    $[8] = deepLinkPostId;
    $[9] = post.id;
    $[10] = session?.mode;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== deepLinkPostId || $[13] !== post.id) {
    t10 = [deepLinkPostId, post.id];
    $[12] = deepLinkPostId;
    $[13] = post.id;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  if ($[15] !== agent || $[16] !== deepLinkPostId || $[17] !== hasCardAI || $[18] !== post.id) {
    t11 = () => {
      if (!hasCardAI || !agent) {
        return;
      }
      const postId = String(post.id);
      let ignore = false;
      agent.cardSessions().then(sessions => {
        if (ignore) {
          return;
        }
        const s = sessions[postId];
        setSession(s ? {
          phase: s.phase,
          mode: s.mode
        } : null);
        setTodos(s?.todos || []);
        if (s && deepLinkPostId === postId) {
          setModal({
            mode: s.mode
          });
        }
      });
      const unsub = agent.onSessionUpdate(data => {
        if (String(data.postId) !== postId) {
          return;
        }
        setSession({
          phase: data.phase,
          mode: data.mode
        });
        setTodos(data.todos || []);
      });
      return () => {
        ignore = true;
        unsub();
      };
    };
    $[15] = agent;
    $[16] = deepLinkPostId;
    $[17] = hasCardAI;
    $[18] = post.id;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== agent || $[21] !== hasCardAI || $[22] !== post.id) {
    t12 = [hasCardAI, agent, post.id];
    $[20] = agent;
    $[21] = hasCardAI;
    $[22] = post.id;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[24] !== popover) {
    t13 = () => {
      if (!popover) {
        return;
      }
      const handler = e => {
        const target = e.target as Node;
        if (dropdownRef.current?.contains(target) || portalRef.current?.contains(target)) {
          return;
        }
        setPopover(null);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    };
    t14 = [popover];
    $[24] = popover;
    $[25] = t13;
    $[26] = t14;
  } else {
    t13 = $[25];
    t14 = $[26];
  }
  useEffect(t13, t14);
  const t15 = isGhost ? `ghost-post-${post.id}-${factiiiId}` : `post-${post.id}`;
  let t16;
  if ($[27] !== factiiiId || $[28] !== post) {
    t16 = {
      type: "post",
      post,
      factiiiId
    };
    $[27] = factiiiId;
    $[28] = post;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = isMobile || isGhost || !canMove;
  let t18;
  if ($[30] !== t15 || $[31] !== t16 || $[32] !== t17) {
    t18 = {
      id: t15,
      data: t16,
      disabled: t17
    };
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t18);
  let t19;
  if ($[34] !== transform) {
    t19 = CSS.Transform.toString(transform);
    $[34] = transform;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const t20 = isDragging ? 0.6 : 1;
  let t21;
  if ($[36] !== t19 || $[37] !== t20 || $[38] !== transition) {
    t21 = {
      transform: t19,
      transition,
      opacity: t20
    };
    $[36] = t19;
    $[37] = t20;
    $[38] = transition;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  const style = t21;
  const t22 = session?.phase ?? null;
  let t23;
  if ($[40] !== t22) {
    t23 = getAIStatus(t22);
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const aiStatus = t23;
  let t24;
  if ($[42] !== config.claudeLoggedIn || $[43] !== config.codexLoggedIn || $[44] !== config.loading) {
    t24 = config.loading ? ["claude", "codex"] : [...(config.claudeLoggedIn ? ["claude"] as const : []), ...(config.codexLoggedIn ? ["codex"] as const : [])];
    $[42] = config.claudeLoggedIn;
    $[43] = config.codexLoggedIn;
    $[44] = config.loading;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  const availableProviders = t24;
  const hasActiveTodos = todos.length > 0 && session?.phase != null;
  const hasChanges = session?.phase === "complete";
  const canSelect = selectable && (session?.phase == null || hasChanges) && (selectionHasChanges == null || selectionHasChanges === hasChanges);
  let t25;
  if ($[46] !== hasActiveTodos || $[47] !== session) {
    t25 = e_0 => {
      e_0.stopPropagation();
      if (hasActiveTodos) {
        setPopover(_temp);
        return;
      }
      if (session?.phase != null) {
        setModal({
          mode: session.mode
        });
        return;
      }
      setPopover(_temp2);
    };
    $[46] = hasActiveTodos;
    $[47] = session;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  const handleIconClick = t25;
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = provider => {
      setPopover(null);
      setModal({
        mode: "bare",
        provider
      });
    };
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  const startTerminal = t26;
  let t27;
  if ($[50] !== deepLinkPostId || $[51] !== pathname || $[52] !== post.id || $[53] !== router || $[54] !== searchParams) {
    t27 = () => {
      setModal(null);
      if (deepLinkPostId === String(post.id) && pathname) {
        const params = new URLSearchParams(searchParams?.toString());
        params.delete("card");
        const qs = params.toString();
        router.replace(qs ? `${pathname}?${qs}` : pathname, {
          scroll: false
        });
      }
    };
    $[50] = deepLinkPostId;
    $[51] = pathname;
    $[52] = post.id;
    $[53] = router;
    $[54] = searchParams;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  const closeSessionModal = t27;
  const t28 = isGhost ? undefined : setNodeRef;
  const t29 = isGhost ? undefined : style;
  let t30;
  if ($[56] !== attributes || $[57] !== isGhost) {
    t30 = isGhost ? {} : attributes;
    $[56] = attributes;
    $[57] = isGhost;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== isGhost || $[60] !== listeners) {
    t31 = isGhost ? {} : listeners;
    $[59] = isGhost;
    $[60] = listeners;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  const t32 = `group transition-all duration-150 ${isGhost ? "pointer-events-none opacity-70" : isDragging ? "z-50 rotate-6 cursor-grabbing opacity-75" : isMobile ? "" : "cursor-grab"}`;
  const t33 = `rounded-lg border bg-background-light transition-all duration-150 hover:bg-background-light ${selected ? "border-primary ring-1 ring-primary" : aiStatus.borderClass}`;
  let t34;
  if ($[62] !== canSelect || $[63] !== hasChanges || $[64] !== isGhost || $[65] !== onToggleSelect || $[66] !== selected) {
    t34 = (canSelect || selected) && !isGhost && <button onPointerDown={_temp3} onClick={e_2 => {
      e_2.stopPropagation();
      onToggleSelect?.(hasChanges);
    }} title={selected ? "Deselect card" : "Select card to merge"} className={`float-left ml-3 mt-4 flex size-4 items-center justify-center rounded-[5px] border transition-colors ${selected ? "border-primary bg-primary text-white" : "border-ds-border-standard bg-background-secondary text-transparent hover:border-primary hover:text-primary/50"}`}><Check size={10} /></button>;
    $[62] = canSelect;
    $[63] = hasChanges;
    $[64] = isGhost;
    $[65] = onToggleSelect;
    $[66] = selected;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  let t35;
  if ($[68] !== aiStatus || $[69] !== availableProviders || $[70] !== handleIconClick || $[71] !== hasActiveTodos || $[72] !== hasCardAI || $[73] !== popover || $[74] !== session?.mode || $[75] !== todos) {
    t35 = hasCardAI && <div className="relative float-left ml-3 mt-3" ref={dropdownRef}><button onPointerDown={_temp4} onClick={handleIconClick} className="rounded-full p-1 transition-colors duration-200 hover:bg-background-light" title={aiStatus.title}>{aiStatus.icon}</button>{popover === "todos" && hasActiveTodos && <TodoDropdown todos={todos} anchorRef={dropdownRef} portalRef={portalRef} onViewFull={() => {
        setPopover(null);
        setModal({
          mode: session?.mode ?? "bare"
        });
      }} />}{popover === "provider" && <ProviderDropdown available={availableProviders} onPick={startTerminal} anchorRef={dropdownRef} portalRef={portalRef} />}</div>;
    $[68] = aiStatus;
    $[69] = availableProviders;
    $[70] = handleIconClick;
    $[71] = hasActiveTodos;
    $[72] = hasCardAI;
    $[73] = popover;
    $[74] = session?.mode;
    $[75] = todos;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== factiiiId || $[78] !== openDetail || $[79] !== post || $[80] !== spaceSlug || $[81] !== uniqueId) {
    t36 = <PostItem post={post} spaceSlug={spaceSlug} isFullLength={false} disableLink={true} onCardClick={openDetail} isInView={true} disableTracking={true} minified={true} ignoreFactiiiId={factiiiId} uniqueId={uniqueId} />;
    $[77] = factiiiId;
    $[78] = openDetail;
    $[79] = post;
    $[80] = spaceSlug;
    $[81] = uniqueId;
    $[82] = t36;
  } else {
    t36 = $[82];
  }
  let t37;
  if ($[83] !== t33 || $[84] !== t34 || $[85] !== t35 || $[86] !== t36) {
    t37 = <div className={t33}>{t34}{t35}{t36}</div>;
    $[83] = t33;
    $[84] = t34;
    $[85] = t35;
    $[86] = t36;
    $[87] = t37;
  } else {
    t37 = $[87];
  }
  let t38;
  if ($[88] !== post.id || $[89] !== t28 || $[90] !== t29 || $[91] !== t30 || $[92] !== t31 || $[93] !== t32 || $[94] !== t37) {
    t38 = <div ref={t28} style={t29} data-id="board-card" data-post-id={post.id} {...t30} {...t31} className={t32}>{t37}</div>;
    $[88] = post.id;
    $[89] = t28;
    $[90] = t29;
    $[91] = t30;
    $[92] = t31;
    $[93] = t32;
    $[94] = t37;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  let t39;
  if ($[96] !== agent || $[97] !== closeSessionModal || $[98] !== modal?.mode || $[99] !== post) {
    t39 = modal?.mode === "claude" && <CardAIModal post={post} mode="claude" onOpenTerminal={() => {
      agent?.cardHandoff(String(post.id)).catch(_temp5);
      setModal({
        mode: "bare"
      });
    }} closeHandler={closeSessionModal} />;
    $[96] = agent;
    $[97] = closeSessionModal;
    $[98] = modal?.mode;
    $[99] = post;
    $[100] = t39;
  } else {
    t39 = $[100];
  }
  let t40;
  if ($[101] !== closeSessionModal || $[102] !== modal || $[103] !== post || $[104] !== spaceSlug) {
    t40 = modal?.mode === "bare" && <BareCardModal post={post} spaceSlug={spaceSlug} initialProvider={modal.provider} closeHandler={closeSessionModal} />;
    $[101] = closeSessionModal;
    $[102] = modal;
    $[103] = post;
    $[104] = spaceSlug;
    $[105] = t40;
  } else {
    t40 = $[105];
  }
  let t41;
  if ($[106] !== detailOpen || $[107] !== post || $[108] !== spaceSlug || $[109] !== uniqueId) {
    t41 = detailOpen && <Modal closeHandler={() => setDetailOpen(false)} maxWidth={1100}><ModalHeader closeHandler={() => setDetailOpen(false)}><h3 className="truncate text-lg font-semibold tracking-tight text-textPrimary">Card</h3></ModalHeader><div className="flex flex-col md:max-h-[calc(90vh-64px)] md:flex-row"><div className="min-w-0 flex-1 p-4 md:overflow-y-auto"><PostItem post={post} spaceSlug={spaceSlug} isFullLength={true} disableLink={true} isInView={true} uniqueId={uniqueId} /><RepliesContainer post={post} isFullLength={true} disableLink={true} uniqueId={uniqueId} /></div><aside className="relative w-full shrink-0 border-t border-ds-border-subtle md:w-72 md:border-l md:border-t-0"><div className="flex flex-col gap-5 p-4 md:absolute md:inset-0 md:overflow-hidden">{post.taggedUsers && post.taggedUsers.length > 0 && <section><h4 className="mb-2 text-sm font-semibold uppercase tracking-wider text-textSecondary">Members</h4><ul className="space-y-2">{post.taggedUsers.map(_temp6)}</ul></section>}<section className="flex min-h-0 flex-1 flex-col"><h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-textSecondary">Activity</h4><CardActivity postId={post.id} /></section></div></aside></div></Modal>;
    $[106] = detailOpen;
    $[107] = post;
    $[108] = spaceSlug;
    $[109] = uniqueId;
    $[110] = t41;
  } else {
    t41 = $[110];
  }
  let t42;
  if ($[111] !== t38 || $[112] !== t39 || $[113] !== t40 || $[114] !== t41) {
    t42 = <>{t38}{t39}{t40}{t41}</>;
    $[111] = t38;
    $[112] = t39;
    $[113] = t40;
    $[114] = t41;
    $[115] = t42;
  } else {
    t42 = $[115];
  }
  return t42;
};
export default BoardCard;
function _temp(prev) {
  return prev === "todos" ? null : "todos";
}
function _temp2(prev_0) {
  return prev_0 === "provider" ? null : "provider";
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}
function _temp4(e_3) {
  return e_3.stopPropagation();
}
function _temp5() {}
function _temp6(u) {
  return <li key={u.id} className="flex items-center gap-2"><Avatar avatarKey={u.avatarKey ?? null} robohash={u.robohash ?? ""} isBot={u.tag === "BOT"} userId={u.id} size={24} /><span className="truncate text-sm text-textPrimary">{u.name ?? `@${u.username}`}</span></li>;
}